export const ROUTES = {
  // auth
  SIGN_IN: "/sign-in",
  SIGN_UP: "/sign-up/:code",
  // VRG-134: self-serve signup — the emailed invitation links here (no invite code).
  SIGN_UP_SELF_SERVE: "/sign-up",
  // Stripe returns here after Checkout. On the PLATFORM, not apps/app: a brand
  // admin fills the form here, so bouncing them to the customer app to be told
  // their payment worked hands them to a different product mid-signup.
  SIGN_UP_SUCCESS: "/signup/success",
  AUTH_CALLBACK: "/auth/callback",
  PASSWORD_SETUP: "/onboarding/password-setup",
  // VERA-1095: unauthenticated "forgot password" completion — the emailed
  // link lands here with a `?token=` from WorkOS. Rendered by the same
  // PasswordSetup component as PASSWORD_SETUP (reset mode is detected by the
  // route), but reached from the UnProtectedRoute group since there is no
  // session at this point.
  RESET_PASSWORD: "/reset-password",
  // VERA-1095: unauthenticated signup email-verification landing — the
  // emailed link lands here with a `?token=`. Mirrors RESET_PASSWORD's
  // shape (UnProtectedRoute, token-in-query, no session required).
  VERIFY_EMAIL: "/verify-email",
  PROFILE: "/profile",
  // VRG-361: the demo console trial's "you're bounced" surface. Reachable while still
  // authenticated (the token is valid, the ACCOUNT is just gated) — a full-page interrupt
  // outside the normal sidebar shell, since most of the app's own data calls now 403.
  TRIAL_ENDED: "/trial-ended",
  // The PUBLIC support page — the App Store / Play Store "support URL", and where
  // the Vera app's own "Contact support" link goes. Reachable signed in OR out, so
  // it is registered outside BOTH route guards (like TRIAL_ENDED above).
  //
  // ⚠️ THIS PATH IS PUBLISHED IN STORE LISTINGS. Once a listing cites it, changing
  // it breaks the support link for every installed build. Treat it like the legal
  // URLs (packages/legal/README.md): if it must move, leave a redirect behind.
  HELP: "/help",

  // main
  HOME: "/",
  SEARCH: "/search",
  TAGS: "/tags",
  TAGS_INVENTORY: `/tags/inventory`,
  // SKUs are minted uppercase (VERA-STK-CNT-25-100); a URL that shouts reads badly, so
  // the path segment is lowercased here -- one place, rather than at each of the ~8 call
  // sites. Lookups are case-insensitive on both the tag-shop and inventory sides, so
  // links already in the wild still resolve. The stored SKU keeps its casing: it is the
  // fulfillment key and existing orders reference it.
  TAG_INVENTORY: (sku: string) => `/tags/inventory/${String(sku).toLowerCase()}`,
  TAGS_SHOP: "/tags/shop",
  // The shop pre-filtered to one Shopify product Type (see constants/tagShopProductTypes).
  // `?type=Hardware` is how "Shop readers" lands on readers rather than the whole catalog.
  TAGS_SHOP_BY_TYPE: (type: string) =>
    `/tags/shop?type=${encodeURIComponent(type)}`,
  TAGS_SHOP_PRODUCT: (id: string) => `/tags/shop/${String(id).toLowerCase()}`,
  TAGS_ORDERS: "/tags/orders",
  // Post-checkout resolver: waits for the webhook to assign the Shopify order id,
  // then redirects to TAGS_ORDER. Keyed by the reconciliation id from checkout.
  TAGS_ORDER_CONFIRMATION: (reconciliationId: string) =>
    `/tags/orders/confirmation/${reconciliationId}`,
  TAGS_ORDER: (id: string) => `/tags/orders/${id}`,
  TAGS_SHOP_CART: "/tags/shop/cart",
  TAGS_SHOP_CHECKOUT: "/tags/shop/cart/checkout",
  PRODUCTS: "/products",
  PRODUCT: (id: string) => `/products/${id}`,
  PRODUCT_CREATE: "/products/create",
  PRODUCT_EDIT: (id: string) => `/products/${id}/edit`,
  CERTIFICATES: "/certificates",
  // Company-wide certificate activity feed (VRG-430) — the full-page view Home's Recent
  // activity card's "View all" links to. A STATIC segment, so it must be registered
  // in App.tsx BEFORE `CERTIFICATE(":id")` — otherwise the dynamic route swallows
  // "activity" as an :id and this 404s.
  //
  // NAMED `COMPANY_…`, not `CERTIFICATES_ACTIVITY`, deliberately: that spelling differs from
  // `CERTIFICATE_ACTIVITY` below — a DIFFERENT page, one certificate's own activity tab — by a
  // single letter. Two constants a keystroke apart, both returning a certificate-activity URL,
  // is a mistake nobody would catch in review.
  COMPANY_CERTIFICATE_ACTIVITY: "/certificates/activity",
  CERTIFICATE: (id: string) => `/certificates/${id}`,
  CERTIFICATE_PROVENANCE: (id: string) => `/certificates/${id}/provenance`,
  CERTIFICATE_SCANS: (id: string) => `/certificates/${id}/scans`,
  CERTIFICATE_ACTIVITY: (id: string) => `/certificates/${id}/activity`,
  // VRG-455 — sales a brand has recorded against a buyer without naming a
  // certificate. Its OWN view, not a tab: a recorded sale is an object the
  // brand creates, searches and voids (§8), and it has no certificate to live
  // under — which is the premise of the feature.
  SALES: "/sales",
  OWNERS: "/people",
  OWNER: (id: string) => `/owners/${id}`,
  OWNER_CREATE: "/owners/create",
  OWNER_EDIT: (id: string) => `/owners/${id}/edit`,
  ANALYTICS: "/analytics",
  // VERA-455: LEGACY. The standalone possession page this addressed is gone — Inventory is a
  // facet of Tags, not a first-class object (§8), so both views are now the one TAGS_INVENTORY
  // tab. Kept solely so App.tsx can redirect old bookmarks there instead of 404ing. Do not
  // link to this; link to TAGS_INVENTORY.
  INVENTORY: "/inventory",
  STUDIO: "/studio",
  DOCUMENTATION: "/documentation",
  REFERRALS: "/settings/referrals",

  // TODO: move payment pages to Billing when Billing gets fleshed out
  PAYMENT_CHECKOUT: `/payment/checkout`,
  PAYMENT_SUCCESS: "/payment/success",

  // settings
  //
  // FLAT LEAF PATHS (VRG-221). Every section used to carry its scope in the URL
  // (/settings/personal/controls, /settings/company/users). The scope segment bought
  // nothing: it made every URL a segment longer and put a crumb in every breadcrumb
  // that no user would say out loud — "Settings / Personal / Controls" where the
  // thing is just Controls. Leaves are now /settings/<section>.
  //
  // The CONSTANT names keep their _PERSONAL_ / _COMPANY_ infix even though the paths
  // dropped it. The scope is still real — it is which MENU a section belongs to in
  // SettingsPage, which is what selects the tab and the nav group — so erasing it
  // from the identifiers would lose information the code still needs, and renaming
  // ~80 call sites would be churn with no reader benefit.
  //
  // The one genuine collision: both supports flattened to /settings/support. They are
  // different surfaces (your own requests vs the whole company's, the latter gated on
  // `support_requests_view_company`), so they are disambiguated by their NAV LABELS,
  // which were already unique — "My requests" and "Support".
  //
  // Old /settings/{personal,company}/* URLs still resolve: App.tsx registers a splat
  // redirect for each scope (LegacySettingsRedirect), so bookmarks and any link
  // already in the wild land on the flat path with query + hash intact.
  SETTINGS: "/settings",
  // Kept as a real landing route, NOT flattened away — /settings/personal and
  // /settings/company render as pages (each opens its tab on that scope's first
  // section). Only the LEAVES lost their scope segment.
  SETTINGS_PERSONAL: "/settings/personal",
  SETTINGS_PERSONAL_PROFILE: "/settings/profile",
  // VRG-490 (D6) — a Security page sibling to Profile: password (moved off
  // Profile, D7) + two-factor authentication enrollment/management.
  SETTINGS_PERSONAL_SECURITY: "/settings/security",
  SETTINGS_PERSONAL_APPEARANCE: "/settings/appearance",
  SETTINGS_PERSONAL_NOTIFICATIONS: "/settings/notifications",
  SETTINGS_PERSONAL_CONTROLS: "/settings/controls",
  // The requests YOU raised. Its company-wide counterpart is
  // SETTINGS_COMPANY_SUPPORT below — the same page at a different scope, which is
  // what let the old in-page tab pair go away (VRG-221).
  //
  // `/settings/my-requests`, not `/settings/support`: flat, the two supports collide,
  // and the tiebreak is the nav label each already carries. This section's label is
  // "My requests" (it says WHOSE they are), so that is its slug; the company one keeps
  // the generic word, where it is correct.
  SETTINGS_PERSONAL_SUPPORT: "/settings/my-requests",
  // VRG-338 — your own slice of the company Activity Log (below), pinned to your own
  // actions. `/settings/my-activity`, not a bare `/settings/activity`: that slug is
  // already the COMPANY page's (SETTINGS_COMPANY_ACTIVITY), and flattening the leaves
  // means a bare word collides — the exact case SETTINGS_PERSONAL_SUPPORT documents
  // above ("my-requests" for the same reason).
  SETTINGS_PERSONAL_ACTIVITY: "/settings/my-activity",
  SETTINGS_COMPANY: "/settings/company",
  SETTINGS_COMPANY_GENERAL: "/settings/general",
  SETTINGS_COMPANY_USERS: "/settings/users",
  SETTINGS_COMPANY_USER: (userId: string) => `/settings/users/${userId}`,
  SETTINGS_COMPANY_ROLES: "/settings/roles",
  SETTINGS_COMPANY_ROLE: (name: string) => `/settings/roles/${name}`,
  SETTINGS_COMPANY_FEATURES: "/settings/features",
  SETTINGS_COMPANY_BILLING: "/settings/billing",
  SETTINGS_COMPANY_BILLING_PLAN: "/settings/billing/plan",
  // Checkout hangs directly off /plan (no redundant "change" segment) so the
  // breadcrumb reads "Billing / Plan / Checkout", not "…/Plan/Change/Checkout".
  SETTINGS_COMPANY_BILLING_PLAN_CHANGE_CHECKOUT: "/settings/billing/plan/checkout",
  // Nested paths keep their remaining depth — only the scope segment was removed, so
  // the breadcrumb still reads "Settings / Billing / Invoices / <id>".
  SETTINGS_COMPANY_INVOICES: `/settings/billing/invoices`,
  SETTINGS_COMPANY_INVOICE: (id: string) => `/settings/billing/invoices/${id}`,
  SETTINGS_COMPANY_PAYMENT_METHODS: "/settings/billing/payment-methods",
  // Everyone at the company's requests. Gated by `support_requests_view_company`,
  // so the nav row is disabled with a tooltip for a user without it (§27) — which
  // is a better home for that gate than the disabled in-page toggle it replaces.
  SETTINGS_COMPANY_SUPPORT: "/settings/support",
  SETTINGS_COMPANY_ADDRESSBOOK: "/settings/addressbook",
  SETTINGS_COMPANY_INSTALLATION: "/settings/installation",
  SETTINGS_COMPANY_INTEGRATIONS: "/settings/integrations",
  // "Fields", not "Field mapping": the page carries BOTH halves of what a company
  // says about its product fields — how an integration's fields map onto Vera's
  // (Mapping) and the company-wide values a product falls back to (Defaults).
  // Mapping alone required a connected integration, so the section was a dead nav
  // row for every company without one.
  SETTINGS_COMPANY_FIELDS: "/settings/fields",
  // The pre-rename path. Kept as a redirect target rather than deleted: it is a real
  // in-app deep link (`?provider=`, `?highlight=sync` from the product page's sync
  // button) and is reachable from bookmarks, so a rename that 404s its own history is
  // a rename that gets reverted (same reasoning as the VRG-221 legacy splat below).
  SETTINGS_COMPANY_FIELDMAPPING_LEGACY: "/settings/field-mapping",
  SETTINGS_COMPANY_INTEGRATION: (name: string) =>`/settings/integrations/${name}`,
  SHOPIFY_OAUTH_CALLBACK: "/integrations/shopify/callback",
  // VRG-459 — the brand-supplied figures analytics metrics are calculated from.
  //
  // ITS OWN PAGE, not a tab on Fields, and the reason is a taxonomy one rather than
  // a naming preference. Fields' tabs complete its name — "Field Mapping" (where a
  // field's data comes from), "Field Defaults" (what a field falls back to) — and
  // there is no honest completion for "your company's annual return-fraud loss".
  // Mapping and Defaults are properties OF A FIELD, schema-level; annual
  // return-fraud loss, gross margin and customer lifetime value are company-level
  // business facts. A different object.
  SETTINGS_COMPANY_BUSINESS_FIGURES: "/settings/business-figures",
  SETTINGS_COMPANY_ACTIVITY: "/settings/activity",

  // support & knowledge base (VRG-224 — see context/proposals/support-knowledge-system-scope.md)
  //
  // Epic A owns this file, so it defines the route constants for the whole
  // support system up front — Epics B (docs) / C (support center + boards)
  // fill in the real pages behind them. Doing it here is what keeps four
  // parallel branches from all editing routes.ts and colliding.
  //
  // Removed here: ROADMAP (never wired — the sidebar pointed "Roadmap" at
  // /help-center instead), HELP_CENTER (was missing its leading slash, and
  // rendered a ReadMe.io iframe we are retiring), LIVE_SUPPORT + STATUS (both
  // rendered a bare <div />). "Live support" is renamed "Contact support" and
  // is now a typed request, not a route; Status links out externally.
  DOCS: "/docs",
  DOC: (category: string, slug: string) => `/docs/${category}/${slug}`,
  // The glossary sits UNDER /docs on a reserved `reference` path rather than beside it:
  // it is documentation, and every tooltip footer in the app deep-links to
  // /docs/reference/glossary#<term-id> (working-conventions §44). `reference` is not a
  // guide category, so no guide can ever collide with this path.
  DOCS_GLOSSARY: "/docs/reference/glossary",
  DOCS_GLOSSARY_TERM: (id: string) => `/docs/reference/glossary#${id}`,
  SUPPORT: "/support",
  SUPPORT_NEW: "/support/new",
  SUPPORT_REQUEST: (id: string) => `/support/${id}`,
  // RETIRED (VRG-324) — a REDIRECT now, not a page. The customer feature board was
  // a destination you had to visit in order to rank things you had already asked
  // for, so almost nobody did; the +1 moved into the intake, where a colleague
  // typing the same thing is offered "add mine to this". The constant stays
  // because the URL is in bookmarks, in What's-new announcement bodies and in
  // request threads we ourselves wrote — `SupportBoardRedirect` sends those
  // readers somewhere useful, permission-aware. Do NOT point new links here; use
  // `useSupportListRoute`. (The public integrations board is unaffected and stays
  // at SETTINGS_COMPANY_INTEGRATIONS.)
  SUPPORT_REQUESTS_BOARD: "/support/requests",
  // Kept: two live consumers (EncodingDeviceSidebarButton, ScanSessionBlock).
  // Epic B4 migrates its content into /docs and turns this into a redirect.
  READER_SETUP: "/support/reader-setup",
  // VRG-221 — the in-app changelog archive. NOT under /support: this is not a request
  // or a conversation, it is product news, and the sidebar Help popover groups it with
  // Status ("what is going on with Vera") rather than with the intake rows ("say
  // something to Vera"). It replaces that popover's old external link to
  // https://vera.inc/whats-new, which could not know who was reading and so could not
  // gate by permission, hide the pre-signup backlog, or say "you asked for this".
  WHATS_NEW: "/whats-new",
  // One announcement, in full. The archive clamps long bodies to three lines, so this
  // is where the rest of a long notice actually lives — and it is the only SHAREABLE
  // address an announcement has ever had. Before it, "read this one" was not a thing
  // you could send someone: the archive is a single URL and a deprecation notice with a
  // deadline was buried in a feed the recipient had to scroll.
  WHATS_NEW_ANNOUNCEMENT: (id: string) => `/whats-new/${id}`,

  //internal
  INTERNAL: "/internal",
  INTERNAL_LABS: "/internal/labs",
  // VRG-684 (Spencer, 2026-10-08) — the sidebar's "Insights" section is now "Analytics", with
  // two rows: Website (the vera.inc funnel, VRG-640) and Product (Tours adoption + Ask, which was
  // /internal/usage). A third row, Revenue (finance, sales over time), is expected later.
  // `/internal/analytics` itself is a REDIRECT to Website, params kept (App.tsx), and stays a
  // path because the SEO/affiliates tab redirects, guides and emails name it.
  INTERNAL_ANALYTICS: "/internal/analytics",
  INTERNAL_ANALYTICS_WEBSITE: "/internal/analytics/website",
  INTERNAL_ANALYTICS_PRODUCT: "/internal/analytics/product",
  // VRG-672 — GTM (go-to-market): how Vera FINDS and WINS customers, as against how the
  // business performs (Analytics) or how the product is used (Usage). `/internal/gtm` is
  // the Channels overview — every Traction channel with its status — and each channel
  // Vera actually OPERATES on gets its own child route. SEO & AEO moved here from
  // `/internal/analytics?tab=seo`, which now redirects (App.tsx).
  INTERNAL_GTM: "/internal/gtm",
  INTERNAL_GTM_SEO: "/internal/gtm/seo",
  // VRG-673 — the trade-show calendar (a read-only snapshot of gtm/channels/trade-shows/events.csv).
  INTERNAL_GTM_TRADE_SHOWS: "/internal/gtm/trade-shows",
  // Traction channel #5 (organic): LinkedIn + Instagram strategy, stat registry and the draft queue.
  // Seven tabs via `?tab=`. Nothing posts unapproved.
  INTERNAL_GTM_SOCIAL: "/internal/gtm/social",
  // VRG-751 (Spencer, 2026-10-23) — GTM → **Insights** (channel #8, "Content marketing" until the
  // rename): ONE page, four path tabs, no nested tab row. The name matches the website section
  // (`/insights`), so the internal name and the public one agree.
  //
  // The split is by OBJECT, not by article type: a perspective and an explainer are the same
  // object on the same idea → draft → approved → published machine, so they are one table with a
  // Type column, not two tabs (VRG-751's recommendation, approved). Interviews are a different
  // object with a different workflow, so they earn a tab. Ideas feed BOTH articles and interviews
  // (VRG-740's `verafied_followup` origin), so they sit at the top level.
  //
  // The index redirects to Articles. Every old address redirects in ONE hop with its params kept
  // (`routes/gtmRedirects.ts`, guarded by `test/gtm-ia-redirect.guard.spec.ts`).
  INTERNAL_GTM_INSIGHTS: "/internal/gtm/insights",
  // VRG-674 — every /insights and /learn article, its status and Spencer's approval. VRG-734 moved
  // it under the parent page and VRG-751 renamed that page; the constant keeps its name so every
  // existing link follows.
  INTERNAL_GTM_ARTICLES: "/internal/gtm/insights/articles",
  /** VRG-674's address, now a redirect to the Articles tab (params kept). Never link to it. */
  INTERNAL_GTM_ARTICLES_LEGACY: "/internal/gtm/articles",
  // VRG-751 — the content-ideas queue (VRG-740), its own tab: ideas feed articles AND interviews.
  INTERNAL_GTM_INSIGHTS_IDEAS: "/internal/gtm/insights/ideas",
  // VRG-734 — the VERAfied series: Guests · Episodes · Formats (`?view=`). VRG-751 calls the tab
  // "Interviews" (the object), keeping VERAfied as the series' own name inside it.
  INTERNAL_GTM_INSIGHTS_INTERVIEWS: "/internal/gtm/insights/interviews",
  // VRG-734 — per-piece content analytics (articles and interviews), consented visitors only.
  INTERNAL_GTM_INSIGHTS_ANALYTICS: "/internal/gtm/insights/analytics",
  INTERNAL_GTM_INSIGHTS_RULES: "/internal/gtm/insights/rules",
  /**
   * VRG-734's addresses, now redirects to the same tab under `/insights` (params kept). Never link
   * to them. The bare `/internal/gtm/content` is included because it was the sidebar's link.
   */
  INTERNAL_GTM_CONTENT_LEGACY: "/internal/gtm/content",
  INTERNAL_GTM_CONTENT_ARTICLES_LEGACY: "/internal/gtm/content/articles",
  INTERNAL_GTM_CONTENT_VERAFIED_LEGACY: "/internal/gtm/content/verafied",
  INTERNAL_GTM_CONTENT_ANALYTICS_LEGACY: "/internal/gtm/content/analytics",
  // VRG-733 — one article (Spencer, 2026-10-22: "clicking the row should go to an article-specific
  // page"), under the Articles tab since the VRG-734 merge (Spencer, 2026-10-29). `section` stays in
  // the path because a slug is unique only within the website section it lives in. Tabs via
  // `?tab=` (`ARTICLE_TABS`, routes/gtmRedirects.ts).
  INTERNAL_GTM_ARTICLE: (section: string, slug: string) => `/internal/gtm/insights/articles/${section}/${slug}`,
  // Its Cover tab: concepts → one image of each → pick → approve (upscale + write to the website).
  INTERNAL_GTM_ARTICLE_COVER: (section: string, slug: string) => `/internal/gtm/insights/articles/${section}/${slug}?tab=cover`,
  // VRG-684 — Traction channel #14 (affiliate programs): "Refer & earn" (brand referrals) and
  // Vera Insiders, as two tabs (`?tab=refer` / `?tab=insiders`). Renamed from Insiders (VRG-678,
  // `/internal/gtm/insiders`), and the People → Referrals tab moved in as part of Refer & earn.
  // Old addresses redirect (App.tsx, `routes/gtmRedirects.ts`).
  INTERNAL_GTM_AFFILIATES: "/internal/gtm/affiliates",
  /** VRG-678's address, now a redirect to Affiliates. Never link to it. */
  INTERNAL_GTM_INSIDERS_LEGACY: "/internal/gtm/insiders",
  // VRG-675 + VRG-684 + VRG-713 — Traction channel #13. One page, tabs in `?tab=` (`SALES_TABS`):
  // Pipeline (default), Deals, Companies, Leads, Campaigns, Playbook, Templates. Campaigns (was
  // "Outreach", key `outreach`, until VRG-713) is PART of Sales (Spencer, 2026-10-08).
  INTERNAL_GTM_SALES: "/internal/gtm/sales",
  /** Breadcrumb parent of a campaign; redirects to `?tab=campaigns`. */
  INTERNAL_GTM_SALES_CAMPAIGNS: "/internal/gtm/sales/campaigns",
  INTERNAL_GTM_SALES_CAMPAIGN: (id: string) => `/internal/gtm/sales/campaigns/${id}`,
  /** VRG-684's campaign addresses, now redirects to the two above (VRG-713). Never link to them. */
  INTERNAL_GTM_SALES_OUTREACH_LEGACY: "/internal/gtm/sales/outreach",
  INTERNAL_GTM_SALES_OUTREACH_CAMPAIGN_LEGACY: (id: string) => `/internal/gtm/sales/outreach/${id}`,
  /** VRG-679's addresses, now redirects into Sales. Never link to them. */
  INTERNAL_GTM_OUTREACH_LEGACY: "/internal/gtm/outreach",
  INTERNAL_GTM_OUTREACH_CAMPAIGN_LEGACY: (id: string) => `/internal/gtm/outreach/${id}`,
  // The Usage page's old address — since VRG-684 a REDIRECT to INTERNAL_ANALYTICS_PRODUCT with
  // its `?tab=` and every other param (App.tsx). The page is the same: Tours + Ask as two tabs
  // with two permissions (the `anyPermission` entries in `internalAccess.ts`).
  INTERNAL_USAGE: "/internal/usage",
  // VRG-227 — a REDIRECT to INTERNAL_ANALYTICS_PRODUCT?tab=ask (App.tsx; one hop). Kept as a route
  // because published guides and glossary entries link to it; deleting the path would
  // 404 them. Remove only after those references are updated and re-published.
  INTERNAL_ASK_STATS: "/internal/ask-stats",
  INTERNAL_ORDERS: "/internal/orders",
  INTERNAL_ORDER: (id: string) => `/internal/orders/${id}`,
  INTERNAL_PACKAGES: "/internal/packages",
  INTERNAL_CREATE_PACKAGE: "/internal/packages",
  INTERNAL_TAGS: "/internal/tags",
  INTERNAL_INVOICES: "/internal/invoices",
  INTERNAL_INVOICE: (id: string) => `/internal/invoices/${id}`,
  // The STAFF list. Labelled "Team" at /internal/team since 2026-10-01, when "People"
  // (and /internal/people) went to the access table below — Spencer's call.
  INTERNAL_USERS: "/internal/team",
  INTERNAL_COMPANIES: "/internal/companies",
  INTERNAL_COMPANY: (id: string) => `/internal/companies/${id}`,
  INTERNAL_CREATE_COMPANY: "/internal/companies/create",
  INTERNAL_EDIT_COMPANY: (id: string) => `/internal/companies/${id}/edit`,
  INTERNAL_SUBSCRIPTIONS: "/internal/subscriptions",
  INTERNAL_INTEGRATIONS: "/internal/integrations",
  // VRG-226 — the cross-company support queue. The only route Epic C adds:
  // Epic A pre-registered the whole company-facing /support surface, but an
  // internal counterpart was not in that set.
  INTERNAL_SUPPORT: "/internal/support",
  // One request, on its own page — the internal twin of SUPPORT_REQUEST above.
  //
  // The queue used to expand a request inline, which made the staff view a
  // DIFFERENT shape from the customer view of the same conversation: no address to
  // paste into Slack, no back-navigation, and the thread rendered in a card list
  // rather than on a page. Parity is the point of `RequestThread` existing at all
  // (read its header note) — a staffer answering a request should be reading it in
  // the same shape the person who raised it does.
  INTERNAL_SUPPORT_REQUEST: (id: string) => `/internal/support/${id}`,
  // VRG-221 — the staff authoring surface for What's new. Its own internal sidebar
  // surface rather than a tab inside /internal/support: an announcement is a
  // first-class object staff create, edit, publish and act on across instances, which
  // by §8's test earns its own view. (The support queue is where a REQUEST lives; this
  // is where the answer to many requests is written.)
  INTERNAL_ANNOUNCEMENTS: "/internal/announcements",
  // VRG-332 — the AUTHORING surface, promoted out of a dialog onto its own two
  // addresses. It had outgrown a modal: title, body, kind, audience, CTA label, CTA
  // route, tour key and a searchable feature-request checklist, in a scrolling box
  // over the list it was editing. Two routes, ONE component (`page="create" | "edit"`),
  // exactly as `ProductEditPage` does — see the page's docblock for why a forked
  // create copy is the thing to avoid (§30).
  //
  // `/new` rather than `/create`, to match the noun the button uses ("New
  // announcement"). The sibling company form uses `/create`; both exist in the app and
  // neither is canonical, so this follows the label the staffer actually clicked.
  //
  // ── WHY EDIT IS `/:id` AND NOT `/:id/edit` ──────────────────────────────────
  // Because the BREADCRUMB has to be the way back, and `/:id/edit` cannot produce a
  // usable one. `Breadcrumbs` renders every path segment, overriding only the LAST:
  //
  //   /internal/announcements/<uuid>/edit  →  Announcements / <raw uuid> / Edit …
  //                                                           ^^^^^^^^^^
  //   a crumb printing a bare uuid, linking to a route that does not exist.
  //
  // The alternatives are worse. Suppressing the id inside the shared `Breadcrumbs` would
  // change every other uuid trail in the app (invoices, orders, requests all deliberately
  // print a NAME there). Falling back to `ReferrerBackLink` does not work either: the
  // list is an ANCESTOR of the form, and the link self-suppresses on exactly that case
  // (see `useHasReferrerBack`) because the breadcrumb is meant to be the single back
  // affordance for a hierarchical drill-in. Which leaves a hand-rolled "← Back", the one
  // thing §42 forbids — and is why `CompanyEditPage` at `/internal/companies/:id/edit`
  // ends up with no wayfinding at all and only Cancel to escape by.
  //
  // Putting the id LAST fixes it outright: the override replaces it with the
  // announcement's title, giving `Announcements / Bulk-export certificates as CSV` with
  // a real parent link. It is also honest about the model — staff have no read-only view
  // of an announcement, so the record's page IS its editor, and inventing a detail page
  // just to hang an /edit off would be a surface nobody asked for.
  //
  // Route ranking makes the pair unambiguous: react-router scores a static segment above
  // a dynamic one, so `/new` wins over `/:id` regardless of declaration order.
  INTERNAL_ANNOUNCEMENT_CREATE: "/internal/announcements/new",
  INTERNAL_ANNOUNCEMENT_EDIT: (id: string) => `/internal/announcements/${id}`,
  // VRG-461 (epic VRG-445) — the market-data corpus. One list page, no detail route:
  // row actions (status, flags) are inline, same shape as InternalPackagesPage.
  //
  // VRG-537 supersedes the "no detail route" half of that comment (the list-only
  // claim above is now wrong and left as history, not fixed, so the VRG-461 reasoning
  // stays legible): problem/solution are multi-line free text and a company
  // multi-select does not belong in a table cell, so this content is the first
  // thing in the epic that needs a real detail surface — see
  // INTERNAL_MARKET_CONTENT below.
  INTERNAL_MARKET: "/internal/content",
  // VRG-462 — the feed registry. Its own view rather than a tab on the corpus:
  // a source is an object staff create, park, re-cadence and poll across
  // instances, which is §8's test for earning one.
  INTERNAL_MARKET_SOURCES: "/internal/content/sources",
  // VRG-463 — the approval queue. Its own route because a proposal is reviewed, not
  // browsed: the reader is deciding, and a tab beside the corpus would invite
  // skimming past it.
  INTERNAL_MARKET_REVIEW: "/internal/content/review",
  // VRG-537 — one piece of content: problem/solution and its assigned companies.
  //
  // `/:id` DIRECTLY under the parent, not `/content/:id` (the task file's literal
  // route string) — deliberately, for the exact reason `INTERNAL_ANNOUNCEMENT_EDIT`
  // above is `/:id` and not `/:id/edit`: `Breadcrumbs` renders EVERY path segment,
  // overriding only the last. A `content` middle segment has no route of its own, so
  // it would render as a dead crumb link ("Market / Content / <title>", "Content"
  // going nowhere) — the exact trap that comment already documents the fix for. Static
  // beats dynamic in react-router's ranking regardless of declaration order, so this
  // still cannot collide with the two static sibling paths above (and item ids are
  // UUIDs, so "sources"/"review" could never arrive here as an id in practice anyway).
  INTERNAL_MARKET_CONTENT: (id: string) => `/internal/content/${id}`,
  // VRG-744 — Competitor research: the competitor directory (moved from GTM → SEO's Competitors
  // tab), price points, the feature matrix, competitors' posts and their search overlap. Its own
  // top-level route, grouped with Market research under the sidebar's "Research" section. A
  // profile is `?tab=directory&competitor=<domain>` (competitorProfilePath), not a sub-route,
  // so every surface that names a competitor can link to one place.
  INTERNAL_COMPETITORS: "/internal/competitors",
  // VRG-224 — the staff-facing docs site. Mirrors DOCS/DOC above and is rendered by the
  // same components, pointed at the internal artifact (see docs/sources.ts). Internal
  // guides had no destination before this, which is how a staff RBAC reference ended up
  // published as a customer page: content with nowhere to live gets mislabelled.
  INTERNAL_DOCS: "/internal/docs",
  INTERNAL_DOC: (category: string, slug: string) =>
    `/internal/docs/${category}/${slug}`,
  // VRG-311 — the AE-facing acquisition demo console. Gated on `demo_provision`
  // (§8: an object AEs operate on directly earns its own sidebar view).
  INTERNAL_DEMOS: "/internal/demos",
  // VRG-401 — the pre-launch signup gate (waitlist + allowlist), graduated out of
  // `/internal/labs/signups`. §8's test settles it: an application awaiting a decision
  // is an object staff operate on directly — approve, reject, invite, comp — across
  // instances, so it earns its own view rather than living as a facet of Labs. Labs is
  // for decisions about the product; this is an operational queue.
  INTERNAL_SIGNUPS: "/internal/signups",
  // Access (VRG-727, Spencer 2026-10-20; "People" from 2026-10-01 until then) — ONE table
  // across the waitlist, the allowlist, pricing access and site-gate invites, replacing the
  // Signups tabs. It lives at `/internal/access` (which was its first address, 2026-10-01).
  // `/internal/people` (its address from 2026-10-01 to VRG-727) and `/internal/signups`
  // redirect here, params and router state kept (`legacyAccessTarget`); the old Signups page
  // stays at `/internal/signups/classic` for the actions Access does not carry. "People" is now
  // GTM → Sales' tab of people on the pipeline. Before 2026-10-01 `/internal/people` was the
  // STAFF list (now INTERNAL_USERS, /internal/team).
  INTERNAL_ACCESS: "/internal/access",
  /** VRG-727 — a REDIRECT to INTERNAL_ACCESS. Kept so bookmarks and published links resolve. */
  INTERNAL_PEOPLE: "/internal/people",
  INTERNAL_SIGNUPS_CLASSIC: "/internal/signups/classic",
} as const;

/**
 * The classic signups page's name, everywhere it is named (its header, the link to it from
 * Access, the "← Back to" a drill-in from it offers). Spencer, 2026-10-08: the old page is
 * "<page> — classic view", never a second page with the same name (and no longer "Signups",
 * which nothing links to). VRG-727: the page is Access now, so "Access — classic view".
 */
export const CLASSIC_ACCESS_TITLE = "Access — classic view";

/**
 * VRG-738 (Spencer, 2026-10-22) — the market-data corpus at `/internal/content` (epic VRG-445)
 * is named here, everywhere it appears: the sidebar row, the page header, the command palette,
 * the parent breadcrumb on a content detail page and the "← Back to" a drill-in from it offers.
 * It was "Content" until GTM → Content marketing arrived (VRG-734 had it as "Market feed" on
 * its branch; Spencer chose "Market research" instead), then VRG-744 (Spencer, 2026-10-29)
 * dropped the "research": the section heading above both rows already says RESEARCH, so the
 * row said it twice. The ROUTE keeps its address — only the name moved — so links already
 * pasted keep working. One constant so those places cannot drift apart on the next rename.
 */
export const MARKET_RESEARCH_TITLE = "Market";

/**
 * VRG-744 — the sidebar row, header, palette row and breadcrumb label read this one constant.
 * "Competitors", not "Competitor research", for the reason above: it sits under RESEARCH.
 */
export const COMPETITOR_RESEARCH_TITLE = "Competitors";

/** VRG-744 — the one address of a competitor's profile; every competitor name links here. */
export const competitorProfilePath = (domain: string) =>
  `/internal/competitors?tab=directory&competitor=${encodeURIComponent(domain)}`;

/**
 * Pages whose name is not their last path segment. Two readers:
 *   - `useReferrerState`'s derived "← Back to <label>" ("/internal/signups/classic" would
 *     otherwise read "Back to Classic");
 *   - `Breadcrumbs` (passed in by `Header`), for every crumb BEFORE the last — the last is
 *     the page's own `breadcrumbsOveride`. VRG-738: without this, a content detail page's
 *     trail read "Content / <title>" from the `content` URL segment.
 * Keyed by bare pathname (no query).
 */
export const PAGE_LABELS: Readonly<Record<string, string>> = {
  [ROUTES.INTERNAL_SIGNUPS_CLASSIC]: CLASSIC_ACCESS_TITLE,
  // The two sibling routes render the same page with a tab preselected (InternalMarketPage).
  [ROUTES.INTERNAL_MARKET]: MARKET_RESEARCH_TITLE,
  [ROUTES.INTERNAL_MARKET_SOURCES]: MARKET_RESEARCH_TITLE,
  [ROUTES.INTERNAL_MARKET_REVIEW]: MARKET_RESEARCH_TITLE,
  [ROUTES.INTERNAL_COMPETITORS]: COMPETITOR_RESEARCH_TITLE,
};

export type Route = (typeof ROUTES)[keyof typeof ROUTES];

/**
 * The only settings sections an INTERNAL Vera user may reach — the "Account" page
 * (VRG-395). Everything else under `/settings` belongs to a brand: General,
 * Installation, Users, Roles, Features, Billing, Support, Address book, Integrations,
 * Fields, Activity log — plus Referrals, which is brand-facing by definition.
 *
 * MY REQUESTS CAME BACK (VRG-378). It left with Referrals on the reasoning above:
 * staff have no company row, so the page either 403'd or resolved to an arbitrary
 * brand. That premise no longer holds. Vera now has its OWN company row
 * (`kind = 'internal'`), staff reports are filed against it, and the API resolves
 * the same company on read as on write — so the page shows a staffer their own
 * requests and nothing else. VRG-395's own commit message called this shot: "if it
 * turns out staff file support requests through that intake, restoring it is one
 * entry in INTERNAL_ACCOUNT_ROUTES". It is, and this is it.
 *
 * It also closes a live inconsistency rather than only adding a page: the Help
 * popover's "My requests" row was never gated, so a staffer clicking it landed on
 * SettingsRoute's redirect to Profile.
 *
 * Vera staff have no company row, so those sections are not merely untidy for them:
 * they are either empty or silently scoped to whichever company happens to resolve.
 * The permission system cannot express this, because an internal role legitimately
 * HOLDS `view_company`, `billing_view` and the rest — it answers "may this role do
 * it", not "does this user have a company at all".
 *
 * An ALLOW-list, not a deny-list, and deliberately so: a personal section added later
 * for brand users must not appear on the Account page by default. Someone has to
 * decide it belongs there.
 *
 * One list, two consumers, and that is the point — `SettingsRoute` gates
 * REACHABILITY and `SettingsPage` filters the NAV from it, so the menu and the URL
 * can never disagree about what exists. Adding a section back for staff (the open
 * question on My requests) is one entry here, not an edit in two files.
 *
 * This is the SECTION list — the four rows of the Account nav, in nav order. The two
 * landing routes are allowed too but are not sections, so they live in
 * `isInternalAccountRoute` rather than here (`SettingsPage` maps this list onto its
 * menu, and a landing route has no menu row).
 */
export const INTERNAL_ACCOUNT_ROUTES: readonly string[] = [
  ROUTES.SETTINGS_PERSONAL_PROFILE,
  // VRG-490 — MUST be reachable by internal staff: `platform_admin` is the
  // one role decision D1 makes 2FA enrollment mandatory for, and staff have
  // no company row, so this allow-list is the only thing standing between
  // them and a 404 on the one page that satisfies that requirement.
  ROUTES.SETTINGS_PERSONAL_SECURITY,
  ROUTES.SETTINGS_PERSONAL_APPEARANCE,
  ROUTES.SETTINGS_PERSONAL_NOTIFICATIONS,
  ROUTES.SETTINGS_PERSONAL_CONTROLS,
  // VRG-378 — see the docblock. Listed last to match its position in
  // `PERSONAL_MENU`, which is what actually orders the nav (`SettingsPage`
  // filters that array by membership of this one rather than mapping this one).
  ROUTES.SETTINGS_PERSONAL_SUPPORT,
] as const;

/**
 * Is this pathname reachable by an internal user — a section above, or a landing route?
 *
 * Sections match on a segment boundary rather than a bare `startsWith`, for the same
 * reason `matchMenuIndex` does: `/settings/profile` must not be read as an ancestor of
 * a future `/settings/profile-imports`.
 *
 * ⚠️ The landing routes are matched EXACTLY, never as prefixes. `/settings` is a prefix
 * of every settings URL in the app, so admitting it as one would let the whole company
 * menu straight through this check — the guard would pass everything and read as
 * working.
 */
export const isInternalAccountRoute = (pathname: string): boolean => {
  if (pathname === ROUTES.SETTINGS || pathname === ROUTES.SETTINGS_PERSONAL) return true;
  return INTERNAL_ACCOUNT_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
};

/**
 * Company sections hidden entirely when the company is on a SINGLE-SEAT plan
 * (VRG-227) — Starter and Individual, `seats: 1` in the Stripe catalog seed.
 *
 * A list of one person has nobody to manage (Users) and no one to hand a role to
 * (Roles & permissions); the company Activity Log is redundant with
 * SETTINGS_PERSONAL_ACTIVITY one level up, which already gives that one person
 * their own trail. Detection is by SEAT LIMIT (`limit === 1`, exactly), not plan
 * name — see `useCompanySeats` / `SeatUsage`. `null` (unlimited, or an unsynced
 * legacy row) and any limit > 1 fall through to the normal split: fail toward
 * showing MORE, since hiding these from a real team would be broken while
 * showing them to a solo user is only clutter.
 *
 * Mirrors `INTERNAL_ACCOUNT_ROUTES`: one list, two consumers — `SettingsRoute`
 * gates REACHABILITY (`isSingleSeatHiddenRoute`, segment-boundary prefix match so
 * nested detail routes like `SETTINGS_COMPANY_USER(id)` and
 * `SETTINGS_COMPANY_ROLE(name)` are covered too) and `SettingsPage` filters the
 * NAV from the same list, so the menu and the URL can never disagree about what
 * exists.
 */
export const SINGLE_SEAT_HIDDEN_ROUTES: readonly string[] = [
  ROUTES.SETTINGS_COMPANY_USERS,
  ROUTES.SETTINGS_COMPANY_ROLES,
  ROUTES.SETTINGS_COMPANY_ACTIVITY,
] as const;

/** Segment-boundary match against `SINGLE_SEAT_HIDDEN_ROUTES` — see its docblock. */
export const isSingleSeatHiddenRoute = (pathname: string): boolean => {
  return SINGLE_SEAT_HIDDEN_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
};

/**
 * VRG-600 — the routes that render with NO session, mirroring exactly the group
 * `App.tsx` wraps in `<UnProtectedRoute />`.
 *
 * Consumed by `utils/colorModeHandoff` to decide whether this origin's stored
 * colour mode is allowed to speak for the person arriving. It is a list of
 * PATTERNS, not paths — `SIGN_UP` is `/sign-up/:code` — so callers match with
 * `matchPath`, never string equality.
 *
 * ⚠️ `TRIAL_ENDED` and `HELP` are NOT here, and their absence is a decision.
 * Both sit outside BOTH route guards (see their docblocks above): `TRIAL_ENDED`
 * is reached holding a valid token, and `HELP` is reachable signed in or out.
 * Neither is a page where "there is no session" is true by construction, which
 * is the only thing this list claims. Membership is defined by the guard group,
 * not by a per-route judgement about how public a page feels — so it cannot
 * drift on someone's opinion, and a guard can check it.
 *
 * Kept honest by `test/pre-auth-routes.guard.spec.ts`, which reads the
 * `<UnProtectedRoute />` block out of `App.tsx` and fails if the two disagree.
 * A new auth route added there without being added here would otherwise show
 * the previous session's theme to a stranger, silently and only sometimes —
 * exactly the class of drift a convention cannot catch.
 */
export const PRE_AUTH_ROUTE_PATTERNS: readonly string[] = [
  ROUTES.SIGN_IN,
  ROUTES.SIGN_UP,
  ROUTES.SIGN_UP_SELF_SERVE,
  ROUTES.SIGN_UP_SUCCESS,
  ROUTES.RESET_PASSWORD,
  ROUTES.VERIFY_EMAIL,
  ROUTES.AUTH_CALLBACK,
] as const;
